import React, { useId } from 'react';

import { Tooltip } from '@components';
import useIsTruncated from '@hooks/useIsTruncated';
import { StatusEntity } from 'types/lead-management';

interface LeadStatusTagProps {
  status?: StatusEntity;
  className?: string;
}

const parseHex = (hex: string): { r: number; g: number; b: number } => {
  const cleanHex = hex.replace('#', '');
  return {
    r: parseInt(cleanHex.substring(0, 2), 16),
    g: parseInt(cleanHex.substring(2, 4), 16),
    b: parseInt(cleanHex.substring(4, 6), 16)
  };
};

const hexToRgba = (hex: string, opacity: number): string => {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const MAX_TEXT_LIGHTNESS = 0.4;

const getReadableTextColor = (hex: string): string => {
  const { r, g, b } = parseHex(hex);
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const lightness = (max + min) / 2;

  if (lightness <= MAX_TEXT_LIGHTNESS) return hex;

  const delta = max - min;
  const saturation =
    delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

  let hue = 0;
  if (delta !== 0) {
    if (max === rn) hue = ((gn - bn) / delta) % 6;
    else if (max === gn) hue = (bn - rn) / delta + 2;
    else hue = (rn - gn) / delta + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }

  return `hsl(${Math.round(hue)}, ${Math.round(
    saturation * 100
  )}%, ${Math.round(MAX_TEXT_LIGHTNESS * 100)}%)`;
};

const LeadStatusTag: React.FC<LeadStatusTagProps> = ({
  status,
  className = ''
}) => {
  const { color = '#f4f4f4', value } = status || {};
  const tooltipId = useId();
  const { ref: textRef, isTruncated } = useIsTruncated<HTMLDivElement>(value);
  return (
    <div
      className={`text-14 flex items-stretch gap-1.5 text-sm font-medium text-gray-700  ${className}`}
    >
      <div
        ref={textRef}
        data-tooltip-id={tooltipId}
        className="flex-1 truncate !rounded-lg px-2 py-1 text-center"
        style={{
          color: getReadableTextColor(color),
          backgroundColor: hexToRgba(color, 0.1),
          maxWidth: '157px'
        }}
      >
        {value}
      </div>
      {isTruncated && (
        <Tooltip id={tooltipId} className="rounded-lg !bg-cofeeNeutral10">
          <div className="max-w-xs whitespace-normal break-words px-2 py-1 text-sm text-cofeeNeutral1">
            {value}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

export default LeadStatusTag;
